<template>
  <!-- Phones: no hover to show the ⋯, so it's always there, above the row's tap; its actions in a
       sheet, an unavailable one saying why (docs/page-layout.md). -->
  <span v-if="isPhone" class="business-intelligence-badge-action-menu__phone" @click.stop>
    <button
      type="button"
      class="business-intelligence-badge-action-menu__phone-button"
      :aria-label="$t('page.business-intelligence.badge.action-menu.more-actions-tooltip')"
      aria-haspopup="dialog"
      @click="openMenu"
    >
      <BaseIcon name="more-horizontal" aria-hidden="true" />
    </button>
    <BaseBottomSheet
      v-model="menu"
      :title="isAddAsAlias ? $t('page.business-intelligence.badge.action-menu.add-alias') : name"
      :can-go-back="isAddAsAlias"
      @back="onAddAsAliasClicked"
    >
      <!-- Suggestions under the field being typed in keep their dropdown. -->
      <div v-if="isAddAsAlias" class="business-intelligence-badge-action-menu__alias">
        <FormAutocomplete
          :value="selectedCompetitor"
          :suggestions="competitors"
          idProp="id"
          labelProp="name"
          hasGrayBackground
          :placeholder="$t('page.business-intelligence.badge.action-menu.search-placeholder')"
          filterByQuery
          clearOnSelect
          @input="onCompetitorSelected"
          :menuMaxHeight="150"
        />
        <p
          v-if="selectedCompetitor && isExistingNormalizedName"
          class="business-intelligence-badge-action-menu__alias-note"
        >
          {{
            $t('page.business-intelligence.badge.action-menu.existing-alias-tooltip', {
              name: name,
              competitorName: selectedCompetitor.name,
            })
          }}
        </p>
        <BaseButton
          v-if="name && selectedCompetitor"
          :disabled="isExistingNormalizedName"
          @click.native="onEditCompetitorClicked"
        >
          {{
            $t('page.business-intelligence.badge.action-menu.add-competitor-alias', {
              name: name,
              competitorName: selectedCompetitor.name,
            })
          }}
        </BaseButton>
      </div>
      <ul v-else class="base-bottom-sheet__menu">
        <li v-for="action in phoneActions" :key="action.key">
          <button type="button" :disabled="!!action.reason" @click="onPhoneAction(action.key)">
            <BaseIcon :name="action.icon" />
            {{ action.label }}
            <BaseIcon v-if="action.key === 'alias'" name="chevron-right" class="base-bottom-sheet__menu-end" />
          </button>
          <p v-if="action.reason" class="base-bottom-sheet__menu-note">{{ action.reason }}</p>
        </li>
      </ul>
    </BaseBottomSheet>
  </span>
  <v-menu
    v-else
    v-model="menu"
    :close-on-content-click="false"
    @click.stop
    offset-y
    offset-x
    :nudge-left="isAddAsAlias ? 300 : 0"
    :nudge-top="isAddAsAlias ? 100 : 0"
    offset-overflow
    :content-class="'business-intelligence-badge-action-menu'"
  >
    <template v-slot:activator="{ on, attrs }">
      <div class="business-intelligence-badge-action-menu__activator">
        <BaseRoundButton
          v-if="hover"
          v-on="on"
          v-bind="attrs"
          isTransparent
          isNeutral
          :tooltip-text="$t('page.business-intelligence.badge.action-menu.more-actions-tooltip')"
          icon="more-horizontal"
          :size="14"
          @click.native.stop="openMenu"
        />
      </div>
    </template>
    <div
      class="business-intelligence-badge-action-menu__content"
      :class="{
        expanded: isAddAsAlias,
      }"
    >
      <div v-if="isAddAsAlias" class="__action-container">
        <div class="__header-container">
          <BaseRoundButton isTransparent icon="chevron-left" :size="16" @click.native="onAddAsAliasClicked" />
          <FormAutocomplete
            :value="selectedCompetitor"
            :suggestions="competitors"
            idProp="id"
            labelProp="name"
            hasGrayBackground
            :placeholder="$t('page.business-intelligence.badge.action-menu.search-placeholder')"
            filterByQuery
            clearOnSelect
            @input="onCompetitorSelected"
            :menuMaxHeight="150"
          />
        </div>
        <BaseButton
          v-if="name && selectedCompetitor"
          :disabled="isExistingNormalizedName"
          @click.native="onEditCompetitorClicked"
          hasIconLeft
          isSmall
          isTransparent
          :tooltip-text="
            isExistingNormalizedName
              ? $t('page.business-intelligence.badge.action-menu.existing-alias-tooltip', {
                  name: name,
                  competitorName: selectedCompetitor.name,
                })
              : ''
          "
        >
          <BaseIcon name="plus" />
          {{
            $t('page.business-intelligence.badge.action-menu.add-competitor-alias', {
              name: name,
              competitorName: selectedCompetitor.name,
            })
          }}
        </BaseButton>
      </div>
      <div v-else class="__action-container base-actions-menu">
        <!-- TODO: refactor this in https://github.com/umaneo/aloha.app/issues/3072 -->
        <ul class="base-actions-menu__ul">
          <li
            class="base-actions-menu__li"
            :class="{
              disabled: isExistingCompetitor || isTenant,
            }"
            @click="onAddCompetitorClicked"
            v-tooltip="
              isExistingCompetitor
                ? $t('page.business-intelligence.badge.action-menu.existing-competitor-tooltip', {
                    name: name,
                  })
                : isTenant
                ? $t('page.business-intelligence.badge.action-menu.is-tenant-tooltip')
                : ''
            "
          >
            <BaseIcon :size="44" name="plus" />
            {{ $t('page.business-intelligence.badge.action-menu.add-competitor') }}
          </li>
          <li
            class="base-actions-menu__li"
            :class="{
              disabled: isCompetitor || isTenant,
            }"
            @click="onAddAsAliasClicked"
            v-tooltip="
              isCompetitor
                ? $t('page.business-intelligence.badge.action-menu.existing-competitor-tooltip', {
                    name: name,
                  })
                : isTenant
                ? $t('page.business-intelligence.badge.action-menu.is-tenant-tooltip')
                : ''
            "
          >
            <BaseIcon :size="44" name="plus" />
            {{ $t('page.business-intelligence.badge.action-menu.add-alias') }}
          </li>
          <li
            v-if="type !== 'winner'"
            class="base-actions-menu__li"
            :class="{
              disabled: hasWinner,
            }"
            @click="onAddWinnerClicked"
            v-tooltip="
              hasWinner
                ? $t('page.business-intelligence.badge.action-menu.has-winner-tooltip', {
                    name: name,
                  })
                : ''
            "
          >
            <BaseIcon :size="44" name="plus" />
            {{ $t('page.business-intelligence.badge.action-menu.add-winner') }}
          </li>
          <li
            v-if="type === 'winner'"
            class="base-actions-menu__li"
            :class="{
              disabled: !isManualEntry,
            }"
            @click="onRemoveWinnerClicked"
            v-tooltip="
              !isManualEntry ? $t('page.business-intelligence.badge.action-menu.delete-official-winner-tooltip') : ''
            "
          >
            <BaseIcon :size="44" name="minus" />
            {{ $t('page.business-intelligence.badge.action-menu.remove-winner') }}
          </li>
        </ul>
      </div>
    </div>
  </v-menu>
</template>
<script>
import { mapGetters } from 'vuex';
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import FormAutocomplete from '@/components/form/FormAutocomplete.vue';
import { normalizeStr } from '@/util/string-util';
import { viewport } from '@/util/viewport';

export default {
  name: 'BIBadgeActionMenu',
  components: { BaseBottomSheet, BaseButton, BaseRoundButton, BaseIcon, FormAutocomplete },
  data: () => ({
    menu: false,
    selectedCompetitor: null,
    isAddAsAlias: false,
    isExistingNormalizedName: false,
  }),
  props: {
    type: {
      type: String, // bidder | winner | potential-bidder
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    isExistingCompetitor: {
      type: Boolean,
      default: false,
    },
    isCompetitor: {
      // is current competitor view
      type: Boolean,
      default: false,
    },
    isTenant: {
      type: Boolean,
      default: false,
    },
    isManualEntry: {
      type: Boolean,
      default: false,
    },
    hover: {
      type: Boolean,
      default: false,
    },
    hasWinner: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('biDashboard', ['competitors']),
    isPhone() {
      return viewport.size === 'phone';
    },
    // The menu's actions, as the sheet lists them: an unavailable one with the reason its
    // tooltip gives on larger screens.
    phoneActions() {
      const t = (key, values) => this.$t(`page.business-intelligence.badge.action-menu.${key}`, values);
      const alreadyThere = (isAlready) => {
        if (isAlready) return t('existing-competitor-tooltip', { name: this.name });
        return this.isTenant ? t('is-tenant-tooltip') : '';
      };
      return [
        {
          key: 'competitor',
          label: t('add-competitor'),
          icon: 'plus',
          reason: alreadyThere(this.isExistingCompetitor),
        },
        { key: 'alias', label: t('add-alias'), icon: 'plus', reason: alreadyThere(this.isCompetitor) },
        ...(this.type === 'winner'
          ? [
              {
                key: 'remove-winner',
                label: t('remove-winner'),
                icon: 'minus',
                reason: this.isManualEntry ? '' : t('delete-official-winner-tooltip'),
              },
            ]
          : [
              {
                key: 'winner',
                label: t('add-winner'),
                icon: 'plus',
                reason: this.hasWinner ? t('has-winner-tooltip', { name: this.name }) : '',
              },
            ]),
      ];
    },
  },
  methods: {
    openMenu() {
      this.menu = true;
    },
    onPhoneAction(key) {
      if (key === 'competitor') this.onAddCompetitorClicked();
      else if (key === 'alias') this.onAddAsAliasClicked();
      else if (key === 'winner') this.onAddWinnerClicked();
      else this.onRemoveWinnerClicked();
    },
    resetState() {
      this.selectedCompetitor = null;
      this.isAddAsAlias = false;
      this.isExistingNormalizedName = false;
    },
    onAddCompetitorClicked() {
      if (!this.isExistingCompetitor || !this.isTenant) {
        this.$emit('add-competitor', this.name);
        this.menu = false;
      }
    },
    onEditCompetitorClicked() {
      this.$emit('edit-competitor', this.selectedCompetitor, normalizeStr(this.name, ' '));
      this.menu = false;
    },
    onCompetitorSelected(competitor) {
      this.selectedCompetitor = competitor;
      this.isExistingNormalizedName = false;
      for (const name of competitor.competitorNames) {
        if (normalizeStr(this.name) === normalizeStr(name.normalizedName)) {
          this.isExistingNormalizedName = true;
          break;
        }
      }
    },
    onAddAsAliasClicked() {
      if (!this.isTenant || !this.isCompetitor) {
        this.isAddAsAlias = !this.isAddAsAlias;
        if (!this.isAddAsAlias) {
          this.selectedCompetitor = undefined;
        }
      }
    },
    onAddWinnerClicked() {
      if (!this.hasWinner) {
        this.$emit('add-winner');
        this.menu = false;
      }
    },
    onRemoveWinnerClicked() {
      this.$emit('remove-winner');
      this.menu = false;
    },
  },
  watch: {
    menu() {
      if (this.menu) {
        this.openMenu();
      } else {
        this.resetState();
      }
    },
  },
};
</script>

<style lang="scss">
.business-intelligence-badge-action-menu {
  // Its glyph keeps the badge's line, its 40px reach past it to take taps.
  &__phone {
    display: inline-flex;
  }

  &__phone-button {
    position: relative;
    z-index: 1;
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin: -12px -12px -12px -8px;
    color: $color-slate-500;

    .base-icon {
      width: 16px;
      height: 16px;
    }
  }

  &__alias {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 8px;
  }

  &__alias-note {
    margin: 0;
    font-size: 12px;
    color: $color-slate-500;
  }

  &__activator {
    .base-round-button {
      margin-left: 4px;

      .base-icon {
        width: 14px;
        height: 14px;
        color: $color-normal !important;
      }
    }
  }

  &__content {
    background-color: $color-white;
    overflow-y: hidden;
    display: flex;
    flex-direction: column;

    &.expanded {
      width: 350px;
      height: 300px;
      padding: 24px 18px;
    }

    .__action-container {
      display: flex;
      flex-direction: column;
      flex: 1 auto;
      overflow-y: hidden;
      gap: 8px;

      > .__header-container {
        display: flex;
        gap: 8px;

        .base-round-button {
          padding-top: 20px;

          .base-icon {
            color: $color-accent !important;
            width: 16px;
            height: 16px;
          }
        }
      }

      &.base-actions-menu {
        li.disabled {
          color: $color-neutral;
          cursor: default;

          &:hover {
            background-color: transparent;
          }
        }
      }
    }
  }
}
</style>
